import { createLogger } from "../utils/logger.js";
import { extractOdomPosition } from "../state/gps-event-coordinator.js";
//#region ../src/ar/tracking-state.ts
/**
* Tracking State Manager
*
* Detects AR tracking loss and restart events, maintaining state
* about the last valid odometry pose for alignment correction.
*
* ARCHITECTURE NOTE: See docs/architecture-ar-gps-pose-separation.md
*
* When WebXR tracking is lost and restarted, the odometry frame resets
* to a new origin. To maintain GPS+AR alignment, we need to:
* 1. Store the last valid pose before tracking was lost
* 2. Detect when tracking resumes with a new pose
* 3. Dispatch odometryTrackingRestarted with both poses for offset calculation
*/
const log = createLogger("TrackingState");
/**
* Possible tracking states
*/
let TrackingState = /* @__PURE__ */ function(TrackingState) {
	/** AR session started but no pose received yet */
	TrackingState["INITIALIZING"] = "initializing";
	/** Actively receiving valid poses */
	TrackingState["TRACKING"] = "tracking";
	/** Pose became unavailable (tracking lost) */
	TrackingState["LOST"] = "lost";
	return TrackingState;
}({});
/**
* Manages AR tracking state and detects loss/restart events.
*/
var TrackingStateManager = class {
	state = "initializing";
	lastValidPose = null;
	lastSensorOrientation = null;
	lostFrameCount = 0;
	originResetDuringLoss = false;
	resetTransform = void 0;
	callbacks;
	constructor(callbacks) {
		this.callbacks = callbacks;
	}
	/**
	* Get current tracking state.
	*/
	getState() {
		return this.state;
	}
	/**
	* Get the last valid AR pose (before tracking was lost).
	*/
	getLastValidPose() {
		return this.lastValidPose;
	}
	/**
	* Get count of consecutive frames without pose (during LOST state).
	*/
	getLostFrameCount() {
		return this.lostFrameCount;
	}
	/**
	* Flag that an XRReferenceSpace reset event occurred during tracking loss.
	* Only meaningful while in LOST state — ignored otherwise.
	*
	* @param transform — Serialized XRReferenceSpaceEvent.transform (post-reset
	*   origin in pre-reset coordinates).  Pass `null` when the event's
	*   transform property is null (runtime can't determine delta).
	*   Omit entirely for backwards-compat with callers that don't have it.
	*/
	markOriginReset(transform) {
		if (this.state === "lost") {
			this.originResetDuringLoss = true;
			this.resetTransform = transform;
		}
	}
	/**
	* Called when a valid pose is received from WebXR.
	*/
	onPoseReceived(pose) {
		if (this.state === "lost") this.handleTrackingRestarted(pose);
		this.state = "tracking";
		this.lastValidPose = pose;
		this.lastSensorOrientation = this.callbacks.getDeviceOrientation();
		this.lostFrameCount = 0;
	}
	/**
	* Called when pose is null/unavailable from WebXR.
	*/
	onPoseLost() {
		this.lostFrameCount++;
		if (this.state === "tracking") {
			this.state = "lost";
			this.callbacks.onTrackingLost();
			log.warn("AR tracking lost");
		}
	}
	/**
	* Reset the manager to initial state.
	* Call this when starting a new session.
	*/
	reset() {
		this.state = "initializing";
		this.lastValidPose = null;
		this.lastSensorOrientation = null;
		this.lostFrameCount = 0;
		this.originResetDuringLoss = false;
		this.resetTransform = void 0;
	}
	/**
	* Handle the transition from LOST to TRACKING (restart detected).
	*/
	handleTrackingRestarted(newPose) {
		const hadReset = this.originResetDuringLoss;
		const savedResetTransform = this.resetTransform;
		this.originResetDuringLoss = false;
		this.resetTransform = void 0;
		if (!hadReset) {
			log.info("AR tracking recovered (same coordinate frame)");
			this.callbacks.onTrackingRecovered?.();
			return;
		}
		if (!this.lastValidPose) {
			log.warn("Tracking restarted but no previous pose available");
			return;
		}
		const currentSensorOrientation = this.callbacks.getDeviceOrientation();
		const lastOrientation = this.lastSensorOrientation ?? currentSensorOrientation;
		const payload = {
			lastValidOdomPos: extractOdomPosition(this.lastValidPose),
			lastValidOdomRot: [
				this.lastValidPose.orientation.x,
				this.lastValidPose.orientation.y,
				this.lastValidPose.orientation.z,
				this.lastValidPose.orientation.w
			],
			lastSensorOrientation: {
				alpha: lastOrientation.alpha,
				beta: lastOrientation.beta,
				gamma: lastOrientation.gamma,
				absolute: lastOrientation.absolute
			},
			newOdomRot: [
				newPose.orientation.x,
				newPose.orientation.y,
				newPose.orientation.z,
				newPose.orientation.w
			],
			newSensorOrientation: {
				alpha: currentSensorOrientation.alpha,
				beta: currentSensorOrientation.beta,
				gamma: currentSensorOrientation.gamma,
				absolute: currentSensorOrientation.absolute
			},
			newOdomPos: extractOdomPosition(newPose),
			resetTransform: savedResetTransform
		};
		log.info("AR tracking restarted (origin reset), dispatching alignment correction");
		this.callbacks.onTrackingRestarted(payload);
	}
};
//#endregion
export { TrackingState, TrackingStateManager };
